import React, { forwardRef } from 'react';
import { formatCurrency, formatDateBR } from '../../../core/utils/format';
import { getInstallmentStatus } from '../../../core/utils/installment';
import { INSTALLMENT_STATUS_COLORS } from '../../../core/constants/financial';
import './FinancialInstallmentsPrint.scss';

// Relatório imprimível de parcelas (Contas a Pagar / Contas a Receber).
// Recebe a lista JÁ FILTRADA da tela (período + status + busca) p/ que o
// impresso corresponda exatamente ao exibido (RN02/RN03/CA03/CA04). Usa
// <table> com table-layout:fixed + <colgroup>: garante que cabeçalho, corpo e
// rodapé compartilhem EXATAMENTE as mesmas larguras de coluna.
const FinancialInstallmentsPrint = forwardRef((props, ref) => {
  const {
    company = {},
    logoBase64,
    reportTitle,
    entityLabel,
    periodLabel,
    statusLabel,
    searchTerm,
    data = [],
  } = props;

  const filtersLine = [
    statusLabel ? `Situação: ${statusLabel}` : '',
    searchTerm ? `Busca: "${searchTerm}"` : '',
  ]
    .filter(Boolean)
    .join(' · ');

  const totalValue = data.reduce((sum, i) => sum + (Number(i.value) || 0), 0);
  const emittedAt = new Date().toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

  return (
    <div ref={ref} className="fin-print">
      <div className="fin-print-topbar">
        <div />
        <span className="fin-topbar-title">{reportTitle}</span>
        <div />
      </div>

      <div className="fin-company-section">
        {logoBase64 && (
          <img className="fin-print-logo" src={logoBase64} alt="Logo da empresa" />
        )}
        <div className="fin-company-details">
          <div className="fin-company-main">
            <div className="fin-company-text">{company.name}</div>
            <div className="fin-company-text">{company.cnpj}</div>
            <div className="fin-company-text">
              {company.street}, N°{company.number}, {company.neighborhood}, {company.city},{' '}
              {company.state}
              {company.complement ? `, ${company.complement}` : ''}
              <br />
              CEP: {company.cep}
            </div>
            <div className="fin-company-text">
              <span>Fone: {company.telephone}</span>
              <span>WhatsApp: {company.cellphone}</span>
            </div>
          </div>

          <div className="fin-company-meta">
            <span>Período: {periodLabel}</span>
            {filtersLine && <span>Filtros: {filtersLine}</span>}
            <span className="fin-emitted">Emitido em: {emittedAt}</span>
          </div>
        </div>
      </div>

      <table className="fin-print-table">
        <colgroup>
          <col className="col-data" />
          <col className="col-desc" />
          <col className="col-entity" />
          <col className="col-due" />
          <col className="col-pay" />
          <col className="col-value" />
          <col className="col-status" />
        </colgroup>

        <thead>
          <tr>
            <th>Data</th>
            <th>Descrição</th>
            <th>{entityLabel}</th>
            <th>Vencimento</th>
            <th>Pgto/Receb.</th>
            <th className="cell-right">Valor</th>
            <th className="cell-center">Situação</th>
          </tr>
        </thead>

        <tbody>
          {data.map((inst, index) => {
            const st = getInstallmentStatus(inst);
            const color = INSTALLMENT_STATUS_COLORS[st];
            const entry = inst.financial_entry || {};
            const description = entry.description || entry.AccountPlan?.name || '—';
            return (
              <tr key={inst.id} className={index % 2 === 0 ? 'row-even' : 'row-odd'}>
                <td>{formatDateBR(entry.issue_date)}</td>
                <td className="cell-text">{description}</td>
                <td className="cell-text">{entry.supplier_client || '—'}</td>
                <td>{formatDateBR(inst.due_date)}</td>
                <td>{formatDateBR(inst.payment_date)}</td>
                <td className="cell-right">{formatCurrency(inst.value)}</td>
                <td className="cell-center">
                  <span className="status-badge" style={{ background: color.bg, color: color.color }}>
                    {color.label}
                  </span>
                </td>
              </tr>
            );
          })}
          {data.length === 0 && (
            <tr>
              <td className="cell-empty" colSpan={7}>Nenhum registro</td>
            </tr>
          )}
        </tbody>

        {data.length > 0 && (
          <tfoot>
            <tr className="footer-row">
              <td className="footer-label" colSpan={5}>
                TOTAL · {data.length} {data.length === 1 ? 'registro' : 'registros'}
              </td>
              <td className="footer-value cell-right">{formatCurrency(totalValue)}</td>
              <td />
            </tr>
          </tfoot>
        )}
      </table>
    </div>
  );
});

FinancialInstallmentsPrint.displayName = 'FinancialInstallmentsPrint';

export default FinancialInstallmentsPrint;
